import type { ContactFieldDto } from '@carbonwebmail/contracts'
import type { Kind, Rule } from './ruleVocabulary.js'
import {
  fieldOptions,
  fieldPatch,
  fieldValue,
  kindOf,
  OPERATORS,
  VALUELESS,
  VALUES,
} from './ruleVocabulary.js'

/**
 * One rule: the field, the comparison, and what it is compared against.
 *
 * Shared by the builder page and the New segment dialog, which ask the same
 * question and must offer the same answers. A second copy would be the place
 * where one screen keeps offering a field the other has stopped translating —
 * and a rule that saves and then matches nobody looks exactly like a broken
 * segment.
 */
export function RuleRow({
  rule,
  declared,
  joiner,
  onChange,
  onRemove,
}: {
  readonly rule: Rule
  readonly declared: readonly ContactFieldDto[]
  readonly joiner: string
  readonly onChange: (patch: Partial<Rule>) => void
  readonly onRemove: () => void
}) {
  const kind = kindOf(rule, declared)
  const fixedValues = rule.field === 'custom' ? undefined : VALUES[rule.field]

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-field border border-line bg-surface-2 px-3 py-2">
      <span className="w-12 font-semibold text-[11px] text-t3 uppercase tracking-[0.06em]">
        {joiner}
      </span>

      <Select
        label="Field"
        value={fieldValue(rule)}
        options={fieldOptions(declared)}
        onChange={(chosen) => onChange(fieldPatch(chosen))}
      />
      <Select
        label="Comparison"
        value={rule.operator}
        options={OPERATORS[kind]}
        onChange={(operator) => onChange({ operator })}
      />

      <RuleValue
        rule={rule}
        kind={kind}
        fixedValues={fixedValues}
        onChange={(value) => onChange({ value })}
      />

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove rule"
        className="ml-auto font-medium text-[13px] text-t3 hover:text-danger"
      >
        Remove
      </button>
    </div>
  )
}

/**
 * What a rule is compared against.
 *
 * Three shapes, and which one appears is the whole of this component. A fixed
 * set gets a select, because "subscribed" is the only spelling the database
 * accepts and letting somebody type it offers a rule that is refused on save
 * for a reason they cannot see. A presence question gets nothing at all:
 * offering a box beside "is empty" asks something the rule does not have, and
 * somebody would fill it in and wonder why it changed nothing.
 */
function RuleValue({
  rule,
  kind,
  fixedValues,
  onChange,
}: {
  readonly rule: Rule
  readonly kind: Kind
  readonly fixedValues: readonly (readonly [string, string])[] | undefined
  readonly onChange: (value: string) => void
}) {
  if (VALUELESS.includes(rule.operator)) return null

  if (fixedValues !== undefined) {
    return <Select label="Value" value={rule.value} options={fixedValues} onChange={onChange} />
  }

  const numeric = kind === 'number' || kind === 'days'

  return (
    <>
      <input
        aria-label="Value"
        value={rule.value}
        inputMode={numeric ? 'numeric' : 'text'}
        placeholder={PLACEHOLDERS[kind]}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 rounded-field border border-line bg-surface px-2.5 py-1.5 text-[13px] text-t1"
      />
      {kind === 'days' ? <span className="text-[13px] text-t3">days</span> : null}
    </>
  )
}

const PLACEHOLDERS: Record<Kind, string> = {
  choice: '',
  text: 'Northwind',
  address: 'someone@example.com',
  number: '12',
  days: '30',
}

/**
 * Generic over what it is choosing between.
 *
 * A `(value: string) => void` here compiles and then loses at every call site:
 * the rule's field is one of four literals, and handing it a widened string is
 * how a select ends up able to produce a field the translation cannot answer.
 */
function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string
  readonly value: string
  readonly options: readonly (readonly [T, string])[]
  readonly onChange: (value: T) => void
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
      className="rounded-field border border-line bg-surface px-2.5 py-1.5 text-[13px] text-t1"
    >
      {/* An empty value is a rule in progress, and the placeholder says so
          rather than pretending the first option was chosen. */}
      {value === '' ? <option value="">Choose…</option> : null}
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </select>
  )
}
